import type { Exercise } from '../exercise-types';

/** Abs, obliques and conditioning finishers. */
export const CORE_EXERCISES: Exercise[] = [
  // ------------------------------------------------------------- flexion ----
  {
    id: 'hanging_leg_raise',
    name: 'Hanging leg raise',
    slots: ['abs_flexion'],
    pattern: 'core',
    primary: ['abs'],
    secondary: ['obliques', 'forearms'],
    equipment: ['pullup_bar'],
    kind: 'compound',
    loadType: 'bodyweight',
    difficulty: 3,
    quality: 10,
    defaultSets: 3,
    repRange: [8, 15],
    restSeconds: 90,
    incrementKg: 0,
    setup: ['Hang from the bar with straight arms, legs together.'],
    cues: [
      'Start by tilting the pelvis and rolling the hips up — that posterior tilt is what trains the lower abs.',
      'If you just swing your legs to 90° from the hip, your hip flexors do the work and the abs do nothing.',
      'Bend the knees to make it easier while you build up.',
    ],
    mistakes: ['Swinging', 'Stopping at 90° without curling the pelvis'],
    variations: ['captains_chair_raise', 'reverse_crunch', 'lying_leg_raise'],
    imagery: {
      start: 'hanging from a pull-up bar with straight arms and legs hanging straight down together',
      end: 'hanging from a bar with legs raised and the pelvis curled up, feet above hip height, abs contracted',
    },
  },
  {
    id: 'captains_chair_raise',
    name: "Captain's chair knee raise",
    slots: ['abs_flexion'],
    pattern: 'core',
    primary: ['abs'],
    secondary: ['obliques'],
    equipment: ['captains_chair'],
    kind: 'compound',
    loadType: 'bodyweight',
    difficulty: 2,
    quality: 8,
    defaultSets: 3,
    repRange: [12, 20],
    restSeconds: 60,
    incrementKg: 0,
    setup: ['Forearms on the pads, back against the rest, legs hanging.'],
    cues: [
      'Curl the knees up towards the chest and lift the hips off the backrest at the top.',
      'Lower slowly — the negative is most of the work.',
    ],
    variations: ['hanging_leg_raise', 'reverse_crunch'],
    imagery: {
      start: "forearms on the pads of a captain's chair with the legs hanging straight down",
      end: "knees curled up towards the chest in a captain's chair with the hips lifted slightly off the backrest",
    },
  },
  {
    id: 'cable_crunch',
    name: 'Cable crunch',
    slots: ['abs_flexion'],
    pattern: 'core',
    primary: ['abs'],
    secondary: ['obliques'],
    equipment: ['cable_station', 'rope_attachment'],
    kind: 'isolation',
    loadType: 'cable',
    difficulty: 1,
    quality: 9,
    defaultSets: 3,
    repRange: [12, 18],
    restSeconds: 60,
    incrementKg: 2.5,
    setup: ['Kneel facing away from or towards a high pulley, rope held beside your head.'],
    cues: [
      'Crunch by rounding the spine down towards your knees — hips stay still.',
      'This is the one ab exercise you can genuinely load heavier over time, which is why it builds thickness.',
    ],
    mistakes: ['Hinging at the hips instead of flexing the spine'],
    variations: ['machine_crunch', 'decline_sit_up'],
    imagery: {
      start: 'kneeling at a high cable holding a rope beside the head, torso upright, hips fixed',
      end: 'kneeling with the torso crunched down towards the knees, spine rounded, rope at the head',
    },
  },
  {
    id: 'machine_crunch',
    name: 'Ab crunch machine',
    slots: ['abs_flexion'],
    pattern: 'core',
    primary: ['abs'],
    secondary: [],
    equipment: ['ab_crunch_machine'],
    kind: 'isolation',
    loadType: 'machine',
    difficulty: 1,
    quality: 7,
    defaultSets: 3,
    repRange: [12, 20],
    restSeconds: 60,
    incrementKg: 5,
    setup: ['Seat set so the pad sits across your chest and the pivot is at your waist.'],
    cues: ['Curl the ribcage towards the pelvis. Do not pull with the arms.'],
    variations: ['cable_crunch', 'decline_sit_up'],
    imagery: {
      start: 'seated upright at an ab crunch machine with the chest pad in place and the torso extended',
      end: 'seated at an ab crunch machine with the torso curled forward and down, abs contracted',
    },
  },
  {
    id: 'decline_sit_up',
    name: 'Decline sit-up',
    slots: ['abs_flexion'],
    pattern: 'core',
    primary: ['abs'],
    secondary: ['obliques'],
    equipment: ['ab_bench'],
    kind: 'compound',
    loadType: 'bodyweight',
    difficulty: 2,
    quality: 7,
    defaultSets: 3,
    repRange: [12, 20],
    restSeconds: 60,
    incrementKg: 2.5,
    setup: ['Hook your feet in on a decline bench, hands crossed on your chest.'],
    cues: [
      'Roll up one vertebra at a time rather than jack-knifing from the hips.',
      'Hold a plate on your chest once bodyweight gets easy.',
    ],
    variations: ['machine_crunch', 'cable_crunch'],
    imagery: {
      start: 'lying back on a decline bench with feet hooked in and arms crossed on the chest',
      end: 'curled up to a seated position on a decline bench with the spine rounded, arms on the chest',
    },
  },
  {
    id: 'reverse_crunch',
    name: 'Reverse crunch',
    slots: ['abs_flexion'],
    pattern: 'core',
    primary: ['abs'],
    secondary: [],
    equipment: ['mat'],
    kind: 'isolation',
    loadType: 'bodyweight',
    difficulty: 1,
    quality: 8,
    defaultSets: 3,
    repRange: [15, 20],
    restSeconds: 45,
    incrementKg: 0,
    setup: ['Lie on your back, hands by your sides or under your hips, knees bent at 90°.'],
    cues: [
      'Lift the hips off the floor by curling the pelvis towards your ribs — a small, deliberate movement.',
      'Best lower-ab option that needs no equipment at all.',
    ],
    variations: ['hanging_leg_raise', 'lying_leg_raise'],
    imagery: {
      start: 'lying on the back on a mat with knees bent at 90 degrees and hips on the floor',
      end: 'lying on the back with the hips curled up off the mat and the knees drawn towards the chest',
    },
  },
  {
    id: 'lying_leg_raise',
    name: 'Lying leg raise',
    slots: ['abs_flexion'],
    pattern: 'core',
    primary: ['abs'],
    secondary: [],
    equipment: ['mat'],
    kind: 'isolation',
    loadType: 'bodyweight',
    difficulty: 1,
    quality: 7,
    defaultSets: 3,
    repRange: [12, 20],
    restSeconds: 45,
    incrementKg: 0,
    setup: ['Lie flat, hands under your glutes, legs straight.'],
    cues: [
      'Press your lower back into the floor and keep it there the whole set.',
      'Lower the legs only as far as you can without the back arching up.',
    ],
    variations: ['reverse_crunch', 'hanging_leg_raise'],
    imagery: {
      start: 'lying flat on a mat with legs straight and raised vertically above the hips',
      end: 'lying flat with straight legs lowered to just above the floor, lower back pressed down',
    },
  },

  // ------------------------------------------------------ anti-extension ----
  {
    id: 'plank',
    name: 'Plank',
    slots: ['abs_antiextension'],
    pattern: 'core',
    primary: ['abs'],
    secondary: ['obliques', 'glutes'],
    equipment: ['mat'],
    kind: 'isolation',
    loadType: 'timed',
    difficulty: 1,
    quality: 7,
    defaultSets: 3,
    repRange: [30, 60],
    restSeconds: 45,
    incrementKg: 0,
    setup: ['Forearms under your shoulders, body in one straight line, toes tucked.'],
    cues: [
      'Squeeze glutes, tuck the ribs down, pull the elbows towards your toes without moving them.',
      'Reps are seconds. If you can hold two minutes, make it harder rather than longer.',
    ],
    variations: ['ab_wheel_rollout', 'hollow_body_hold'],
    imagery: {
      start: 'forearm plank position on a mat, body in one straight line from head to heels, side view',
      end: 'holding a rigid forearm plank with the hips level and the ribs tucked, side view',
    },
  },
  {
    id: 'ab_wheel_rollout',
    name: 'Ab wheel rollout',
    slots: ['abs_antiextension'],
    pattern: 'core',
    primary: ['abs'],
    secondary: ['lats', 'obliques'],
    equipment: ['ab_wheel', 'mat'],
    kind: 'compound',
    loadType: 'bodyweight',
    difficulty: 3,
    quality: 10,
    defaultSets: 3,
    repRange: [8, 12],
    restSeconds: 90,
    incrementKg: 0,
    setup: ['Kneel on a mat holding the wheel under your shoulders.'],
    cues: [
      'Roll out slowly keeping the hips tucked under and the lower back flat.',
      'Only go as far as you can hold that position — the moment your back arches, you have gone too far.',
      'The single best anti-extension exercise. Brutal and worth it.',
    ],
    mistakes: ['Letting the lower back sag into extension'],
    variations: ['plank', 'hanging_leg_raise'],
    imagery: {
      start: 'kneeling on a mat holding an ab wheel directly under the shoulders, back flat',
      end: 'rolled out with the ab wheel far in front, body extended in a straight line just above the floor',
    },
  },
  {
    id: 'hollow_body_hold',
    name: 'Hollow body hold',
    slots: ['abs_antiextension'],
    pattern: 'core',
    primary: ['abs'],
    secondary: [],
    equipment: ['mat'],
    kind: 'isolation',
    loadType: 'timed',
    difficulty: 2,
    quality: 8,
    defaultSets: 3,
    repRange: [20, 40],
    restSeconds: 60,
    incrementKg: 0,
    setup: ['Lie on your back, press the lower back flat into the floor.'],
    cues: [
      'Lift the shoulder blades and legs off the floor into a shallow banana shape.',
      'The lower back must stay pressed down. Bend the knees to regress. Reps are seconds.',
    ],
    variations: ['plank', 'dead_bug'],
    imagery: {
      start: 'lying on the back on a mat with arms overhead and legs straight, ready to lift',
      end: 'hollow body hold with shoulders and straight legs lifted off the mat, lower back pressed flat',
    },
  },
  {
    id: 'dead_bug',
    name: 'Dead bug',
    slots: ['abs_antiextension'],
    pattern: 'core',
    primary: ['abs'],
    secondary: ['obliques'],
    equipment: ['mat'],
    kind: 'isolation',
    loadType: 'bodyweight',
    difficulty: 1,
    quality: 7,
    defaultSets: 3,
    repRange: [10, 16],
    restSeconds: 45,
    incrementKg: 0,
    setup: ['On your back, arms straight up, hips and knees bent to 90°.'],
    cues: [
      'Lower the opposite arm and leg slowly while keeping the lower back glued to the floor.',
      'Breathe out as you extend. Great warm-up as well as core work.',
    ],
    variations: ['hollow_body_hold', 'plank'],
    imagery: {
      start: 'lying on the back with both arms pointing up and knees bent at 90 degrees above the hips',
      end: 'dead bug extended position, one arm overhead and the opposite leg straight just above the floor',
    },
  },

  // ------------------------------------------------------------ obliques ----
  {
    id: 'cable_woodchop',
    name: 'Cable woodchop',
    slots: ['obliques'],
    pattern: 'core',
    primary: ['obliques'],
    secondary: ['abs'],
    equipment: ['cable_station', 'single_handle'],
    kind: 'compound',
    loadType: 'cable',
    difficulty: 2,
    quality: 8,
    unilateral: true,
    defaultSets: 3,
    repRange: [12, 15],
    restSeconds: 60,
    incrementKg: 2.5,
    setup: ['Handle on a high pulley, stand side-on and grip it with both hands.'],
    cues: [
      'Pull down and across your body to the opposite hip, pivoting the back foot.',
      'Rotate from the ribcage, not the lower back.',
    ],
    variations: ['russian_twist', 'side_plank'],
    imagery: {
      start: 'standing side on to a high cable holding the handle with both hands up beside one shoulder',
      end: 'handle pulled down and across the body to the opposite hip, torso rotated, back foot pivoted',
    },
  },
  {
    id: 'russian_twist',
    name: 'Russian twist',
    slots: ['obliques'],
    pattern: 'core',
    primary: ['obliques'],
    secondary: ['abs'],
    equipment: ['med_ball', 'mat'],
    kind: 'isolation',
    loadType: 'bodyweight',
    difficulty: 1,
    quality: 6,
    defaultSets: 3,
    repRange: [16, 24],
    restSeconds: 45,
    incrementKg: 2,
    setup: ['Sit with knees bent, lean back to about 45°, holding a medicine ball at your chest.'],
    cues: [
      'Rotate the shoulders and ribcage side to side, touching the ball down beside each hip.',
      'Count each side as a rep. Keep the chest tall rather than collapsing.',
    ],
    variations: ['cable_woodchop', 'side_plank'],
    imagery: {
      start: 'seated leaning back at 45 degrees with knees bent holding a medicine ball at the chest',
      end: 'seated leaning back with the torso rotated and the medicine ball touched down beside one hip',
    },
  },
  {
    id: 'side_plank',
    name: 'Side plank',
    slots: ['obliques'],
    pattern: 'core',
    primary: ['obliques'],
    secondary: ['abs', 'glutes'],
    equipment: ['mat'],
    kind: 'isolation',
    loadType: 'timed',
    difficulty: 1,
    quality: 7,
    unilateral: true,
    defaultSets: 3,
    repRange: [30, 45],
    restSeconds: 45,
    incrementKg: 0,
    setup: ['Forearm under the shoulder, feet stacked, hips lifted.'],
    cues: ['One straight line from head to heels. Reps are seconds, per side.'],
    variations: ['copenhagen_plank', 'suitcase_carry'],
    imagery: {
      start: 'side plank on the forearm with the hips lifted and feet stacked, body in a straight line',
      end: 'holding a rigid side plank with the top arm reaching up towards the ceiling',
    },
  },
  {
    id: 'suitcase_carry',
    name: 'Suitcase carry',
    slots: ['obliques', 'finisher'],
    pattern: 'carry',
    primary: ['obliques'],
    secondary: ['abs', 'traps', 'forearms'],
    equipment: ['dumbbells'],
    kind: 'compound',
    loadType: 'timed',
    difficulty: 1,
    quality: 8,
    unilateral: true,
    defaultSets: 3,
    repRange: [30, 45],
    restSeconds: 60,
    incrementKg: 2,
    setup: ['One heavy dumbbell in one hand only, stand tall.'],
    cues: [
      'Resist the pull sideways — walk without leaning. That resistance is the exercise.',
      'Swap sides each set. Reps are seconds.',
    ],
    variations: ['farmers_walk', 'side_plank'],
    imagery: {
      start: 'standing tall holding a single heavy dumbbell in one hand at the side, shoulders level',
      end: 'walking forward carrying one heavy dumbbell on one side, torso staying perfectly upright',
    },
  },
  {
    id: 'hanging_oblique_raise',
    name: 'Hanging oblique knee raise',
    slots: ['obliques'],
    pattern: 'core',
    primary: ['obliques'],
    secondary: ['abs', 'forearms'],
    equipment: ['pullup_bar'],
    kind: 'compound',
    loadType: 'bodyweight',
    difficulty: 3,
    quality: 8,
    unilateral: true,
    defaultSets: 3,
    repRange: [10, 15],
    restSeconds: 60,
    incrementKg: 0,
    setup: ['Hang from the bar, legs together.'],
    cues: ['Curl the knees up and towards one armpit, alternating sides. Control the swing.'],
    variations: ['hanging_leg_raise', 'cable_woodchop'],
    imagery: {
      start: 'hanging from a pull-up bar with legs straight down together, arms straight',
      end: 'hanging from a bar with the knees curled up and rotated towards one side of the ribcage',
    },
  },

  // ----------------------------------------------------------- finishers ----
  {
    id: 'kettlebell_swing',
    name: 'Kettlebell swing',
    slots: ['finisher', 'glutes'],
    pattern: 'hinge',
    primary: ['glutes', 'hamstrings'],
    secondary: ['abs', 'lower_back', 'forearms'],
    equipment: ['kettlebells'],
    kind: 'compound',
    loadType: 'timed',
    difficulty: 2,
    quality: 9,
    defaultSets: 4,
    repRange: [15, 25],
    restSeconds: 60,
    incrementKg: 4,
    setup: ['Kettlebell a foot in front of you, hinge and hike it back between your legs.'],
    cues: [
      'It is a hip snap, not a squat and not a front raise — the bell floats up because your hips threw it.',
      'Finish every rep standing tall with glutes and abs locked. Brilliant conditioning with zero joint cost.',
    ],
    mistakes: ['Squatting the swing', 'Lifting the bell with the arms'],
    variations: ['cable_pull_through', 'burpee'],
    imagery: {
      start: 'hinged forward with a kettlebell hiked back between the legs, flat back, arms straight',
      end: 'standing tall with the hips snapped forward and the kettlebell floating up to chest height, arms straight',
    },
  },
  {
    id: 'burpee',
    name: 'Burpee',
    slots: ['finisher'],
    pattern: 'core',
    primary: ['quads', 'chest'],
    secondary: ['abs', 'delts_front', 'triceps'],
    equipment: ['bodyweight'],
    kind: 'compound',
    loadType: 'timed',
    difficulty: 2,
    quality: 7,
    defaultSets: 3,
    repRange: [10, 20],
    restSeconds: 60,
    incrementKg: 0,
    setup: ['Stand tall with room around you.'],
    cues: ['Drop to a push-up, jump the feet back in, explode up. Steady pace beats a sprint you cannot hold.'],
    variations: ['kettlebell_swing', 'push_up'],
    imagery: {
      start: 'standing upright with arms overhead at the top of a burpee jump',
      end: 'bottom of a burpee in a push-up position with the chest near the floor, legs extended back',
    },
  },
];
